import { useEffect, useMemo, useRef, useState } from "react";
import { api, configurarGateway, desregistrarObjectUrl, type ArtefactoExpediente, type DetalleExpediente, type FilaBandeja, type FrescuraProyeccion, type SaludGateway } from "./api";
import { VisorArtefacto } from "./VisorArtefacto";

function useEnParam(): [string | null, (v: string | null) => void] {
  const [actual, setActual] = useState<string | null>(
    () => new URLSearchParams(location.search).get("exp"),
  );
  const set = (v: string | null) => {
    const url = new URL(location.href);
    if (v) url.searchParams.set("exp", v);
    else url.searchParams.delete("exp");
    history.replaceState(null, "", url);
    setActual(v);
  };
  return [actual, set];
}

function useParam(nombre: string): [string | null, (v: string | null) => void] {
  const [actual, setActual] = useState<string | null>(
    () => new URLSearchParams(location.search).get(nombre),
  );
  const set = (v: string | null) => {
    const url = new URL(location.href);
    if (v) url.searchParams.set(nombre, v);
    else url.searchParams.delete(nombre);
    history.replaceState(null, "", url);
    setActual(v);
  };
  return [actual, set];
}

export function App() {
  const [exp, setExp] = useEnParam();
  const [artefacto, setArtefacto] = useParam("artefacto");
  const [usuario, setUsuario] = useState("…");
  const [ambiente, setAmbiente] = useState("AMBIENTE NO VERIFICADO");
  const [sesionCargada, setSesionCargada] = useState(false);
  const [gatewayListo, setGatewayListo] = useState(false);
  const [salud, setSalud] = useState<SaludGateway | null>(null);
  const [saludError, setSaludError] = useState(false);

  useEffect(() => {
    api.sesion().then(async (s) => {
        setUsuario(s.usuario);
        setAmbiente(s.etiqueta_ambiente);
        if (!s.gateway_navegador_url) return;
        // H4: cookie fallback ONLY in explicit local development.
        configurarGateway(s.gateway_navegador_url, s.ambiente === "DESARROLLO_LOCAL");
        try {
          const estadoSalud = await api.salud();
          setSalud(estadoSalud);
          setGatewayListo(true);
        } catch {
          setSaludError(true);
        }
      })
      .catch(() => {
        setUsuario("sin sesión");
        setAmbiente("AMBIENTE NO VERIFICADO");
      })
      .finally(() => setSesionCargada(true));
  }, []);

  return (
    <>
      <header className="barra">
        <span className="producto">Indubia Expedientes</span>
        <span className="pill">{ambiente}</span>
        <span className={`salud ${saludError ? "mal" : "ok"}`} role="status">
          {saludError ? "⚠ servidor local no responde" : salud ? `gateway ✓ · disco ${salud.disco_raiz_libre_gb} GB` : "…"}
        </span>
        <span style={{ marginLeft: "auto" }}>usuario: {usuario}</span>
      </header>
      <main>
        <div className="aviso" role="note">
          La decisión corresponde al abogado revisor. La IA propone; las reglas controlan; usted decide.
        </div>
        {!sesionCargada ? (
          <p role="status">verificando ambiente y canal local…</p>
        ) : !gatewayListo ? (
          <p role="alert">Operación bloqueada: Gateway local no configurado o no verificado.</p>
        ) : artefacto ? (
          <VisorArtefacto artefactoId={artefacto} volver={() => setArtefacto(null)} />
        ) : exp ? (
          <Detalle id={exp} volver={() => setExp(null)} abrirArtefacto={setArtefacto} />
        ) : (
          <Bandeja abrir={setExp} />
        )}
      </main>
    </>
  );
}

function edadLegible(timestamp: string | null, ahora = Date.now()): string {
  if (!timestamp) return "edad desconocida";
  const milisegundos = ahora - Date.parse(timestamp);
  if (!Number.isFinite(milisegundos)) return "edad desconocida";
  const segundos = Math.max(0, Math.floor(milisegundos / 1000));
  if (segundos < 60) return "menos de un minuto";
  const minutos = Math.floor(segundos / 60);
  if (minutos < 60) return `${minutos} minuto${minutos === 1 ? "" : "s"}`;
  const horas = Math.floor(minutos / 60);
  if (horas < 24) return `${horas} hora${horas === 1 ? "" : "s"}`;
  const días = Math.floor(horas / 24);
  return `${días} día${días === 1 ? "" : "s"}`;
}

export function mensajeFrescura(proyeccion: FrescuraProyeccion, ahora = Date.now()): string {
  const activa = proyeccion.activa;
  const edadActiva = activa.completada_en ?? activa.observada_en;
  switch (proyeccion.ultimo_intento.estado) {
    case "completada":
      return activa.sync_id
        ? `Proyección actualizada hace ${edadLegible(edadActiva, ahora)}.`
        : "Último intento completado, pero no hay una proyección activa.";
    case "fallida":
      return activa.sync_id
        ? `Advertencia: falló el último intento; datos vigentes actualizados hace ${edadLegible(edadActiva, ahora)}.`
        : "Advertencia: falló el último intento y no hay una proyección activa.";
    case "descartada":
      return activa.sync_id
        ? `Último intento descartado por obsoleto; datos vigentes actualizados hace ${edadLegible(edadActiva, ahora)}.`
        : "Último intento descartado; no hay una proyección activa.";
    case "iniciada":
      return "Sincronización iniciada; se verificará una sola vez si hay una nueva proyección.";
    case "sin_datos":
      return "Aún no hay datos sincronizados en la bandeja.";
  }
}

const syncIdsRefrescados = new Set<string>();
export function limpiarCacheRefrescos(): void {
  syncIdsRefrescados.clear();
}

function Bandeja({ abrir }: { abrir: (id: string) => void }) {
  const [filas, setFilas] = useState<FilaBandeja[]>([]);
  const [proyeccion, setProyeccion] = useState<FrescuraProyeccion | null>(null);
  const [error, setError] = useState("");
  const timer = useRef<number | null>(null);

  useEffect(() => {
    let activo = true;
    const cargar = () => api.bandeja().then((b) => {
      if (!activo) return;
      setFilas(b.expedientes);
      setProyeccion(b.proyeccion);
      const intento = b.proyeccion.ultimo_intento;
      if (intento.estado === "iniciada" && intento.sync_id && !syncIdsRefrescados.has(intento.sync_id)) {
        syncIdsRefrescados.add(intento.sync_id);
        timer.current = window.setTimeout(() => {
          if (activo) void cargar();
        }, 0);
      }
    }).catch((e) => {
      if (activo) setError(String(e));
    });

    void cargar();
    return () => {
      activo = false;
      if (timer.current !== null) window.clearTimeout(timer.current);
    };
  }, []);

  const conteos = useMemo(() => {
    const c = { porRevisar: 0, bloqueados: 0, errores: 0, cerrados: 0 };
    for (const f of filas) {
      if (f.estado === "REQUIERE_REVISION") c.porRevisar++;
      else if (f.estado.startsWith("BLOQUEADO")) c.bloqueados++;
      else if (f.estado === "ERROR_TECNICO") c.errores++;
      else if (f.estado === "CERRADO" || f.estado === "DECIDIDO") c.cerrados++;
    }
    return c;
  }, [filas]);

  return (
    <section aria-label="bandeja de expedientes">
      <div className="tarjetas">
        <Tarjeta num={conteos.porRevisar} rotulo="Por revisar" />
        <Tarjeta num={conteos.bloqueados} rotulo="Bloqueados por antecedente" />
        <Tarjeta num={conteos.errores} rotulo="Error técnico" />
        <Tarjeta num={conteos.cerrados} rotulo="Decididos/cerrados" />
      </div>
      {proyeccion && <p role="status" aria-label="estado de proyección">{mensajeFrescura(proyeccion)}</p>}
      {error && <p role="alert">Error cargando bandeja: {error}</p>}
      <table>
        <thead><tr><th>ID</th><th>Tipo</th><th>Estado</th><th>Críticas</th><th>Medias</th><th>Actualizado</th><th></th></tr></thead>
        <tbody>
          {filas.map((f) => {
            const c = f.conteos ?? JSON.parse(f.conteos_json ?? "{}");
            return <tr key={f.id_nube}>
              <td>{f.id_nube.slice(0, 12)}</td><td>{f.tipo_proceso}</td>
              <td><span className="badge estado">{f.estado}</span></td>
              <td>{c.CRITICA ? <span className="badge CRITICA">{c.CRITICA}</span> : "—"}</td>
              <td>{c.MEDIA ? <span className="badge MEDIA">{c.MEDIA}</span> : "—"}</td>
              <td>{new Date(f.actualizado_en).toLocaleString("es-CL")}</td>
              <td><a className="fila" href={`?exp=${f.id_nube}`} onClick={(e) => { e.preventDefault(); abrir(f.id_nube); }}>revisar</a></td>
            </tr>;
          })}
        </tbody>
      </table>
    </section>
  );
}

function Tarjeta({ num, rotulo }: { num: number; rotulo: string }) {
  return <div className="tarjeta"><div className="num">{num}</div><div className="rotulo">{rotulo}</div></div>;
}

function Detalle({ id, volver, abrirArtefacto }: { id: string; volver: () => void; abrirArtefacto: (id: string) => void }) {
  const [d, setD] = useState<DetalleExpediente | null>(null);
  const [artefactos, setArtefactos] = useState<ArtefactoExpediente[]>([]);
  const [errorArtefactos, setErrorArtefactos] = useState("");
  const [modal, setModal] = useState(false);
  const [error, setError] = useState("");
  const [analizando, setAnalizando] = useState(false);
  const [resultadoAccion, setResultadoAccion] = useState<{ tipo: "success" | "error"; mensaje: string } | null>(null);
  // H5 + N2: object URLs opened from this panel, revoked on unmount/close.
  // N2: URLs are ALSO registered centrally in api.ts so cerrarSesion()
  // revokes them even while this panel stays mounted; unmount unregisters.
  const urlsAbiertos = useRef<string[]>([]);
  useEffect(() => () => {
    for (const u of urlsAbiertos.current) {
      URL.revokeObjectURL(u);
      desregistrarObjectUrl(u);
    }
  }, []);

  const abrirDocumento = async (idNube: string, nombre: string) => {
    try {
      const objectUrl = await api.abrirDocumentoAutenticado(idNube, nombre);
      urlsAbiertos.current.push(objectUrl);
      window.open(objectUrl, "_blank", "noopener");
    } catch (e) {
      setError(`No se pudo abrir el documento (${String(e)})`);
    }
  };

  const cargar = async () => {
    try {
      const detalle = await api.detalle(id);
      setD(detalle);
      setError("");
      return detalle;
    } catch (e) {
      setError(String(e));
      throw e;
    }
  };
  useEffect(() => { void cargar().catch(() => undefined); }, [id]);

  // H-L1: artifact listing served by the Gateway documentary channel.
  useEffect(() => {
    let activo = true;
    api.artefactosExpediente(id)
      .then((r) => { if (activo) setArtefactos(r.artefactos); })
      .catch((e) => { if (activo) setErrorArtefactos(String(e)); });
    return () => { activo = false; };
  }, [id]);

  const analizar = async () => {
    setAnalizando(true);
    setResultadoAccion(null);
    try {
      await api.analizar(id);
      await cargar();
      setResultadoAccion({ tipo: "success", mensaje: "Operación local completada. La proyección nube es eventual; no se garantiza actualización inmediata." });
    } catch (e) {
      setResultadoAccion({ tipo: "error", mensaje: `No se pudo analizar: ${String(e)}` });
    } finally {
      setAnalizando(false);
    }
  };

  if (error) return <p role="alert">Error: {error} <button className="sec" onClick={volver}>volver</button></p>;
  if (!d) return <p>cargando…</p>;
  const critAbiertas = d.incidencias.filter((i) => i.estado === "ABIERTA" && i.severidad === "CRITICA");

  return (
    <section aria-label="detalle de expediente">
      <div className="detalle">
        <div className="panel">
          <h2>Índice documental</h2>
          <p><span className="badge estado">{d.estado}</span> · {d.tipo_proceso} · v{d.version}</p>
          <ul>{d.documentos.map((doc) => <li key={doc.id}><button className="fila" onClick={() => void abrirDocumento(d.id_nube, doc.nombre)}>{doc.nombre}</button>{" "}<small>({doc.tamano} B, sha256 {doc.hash.slice(0, 10)}…)</small></li>)}</ul>
          <h3>Artefactos con evidencia</h3>
          {errorArtefactos && <p role="alert">Error cargando artefactos: {errorArtefactos}</p>}
          {!errorArtefactos && artefactos.length === 0 && <p>Sin artefactos ingestado.</p>}
          {artefactos.length > 0 && (
            <ul aria-label="listado de artefactos">
              {artefactos.map((a) => (
                <li key={a.artifact_id}>
                  {a.visible_visor ? (
                    <button className="fila" onClick={() => abrirArtefacto(a.artifact_id)}>
                      ver en visor: {a.external_name}
                    </button>
                  ) : (
                    <span>{a.external_name}</span>
                  )}{" "}
                  <small>({a.tipo}, {a.size_bytes} B, sha256 {a.sha256.slice(0, 10)}…{a.visible_visor ? "" : ", sin páginas"})</small>
                </li>
              ))}
            </ul>
          )}
          {d.capacidades.analizar && <button className="sec" onClick={() => void analizar()} disabled={analizando}>{analizando ? "analizando…" : "analizar de nuevo"}</button>}
          {d.capacidades.analizar && d.capacidades.decidir && " "}
          {d.capacidades.decidir && <button onClick={() => setModal(true)} disabled={critAbiertas.length > 0 || analizando}>decidir</button>}
          {resultadoAccion && <p role={resultadoAccion.tipo === "error" ? "alert" : "status"}>{resultadoAccion.mensaje}</p>}
          {critAbiertas.length > 0 && <p role="alert"><span className="badge CRITICA">{critAbiertas.length} crítica(s) abierta(s)</span> — debe resolver/descartar antes de aprobar.</p>}
          <p><button className="sec" onClick={volver}>← bandeja</button></p>
        </div>
        <div className="panel">
          <h2>Alertas y decisiones</h2>
          {d.incidencias.length === 0 && <p>Sin incidencias abiertas.</p>}
          {d.incidencias.map((i) => <div key={i.id} className={`incidencia ${i.severidad}`}><strong>[{i.severidad}]</strong> {i.regla_id} — {i.detalle}<br /><small>estado: {i.estado}{i.motivo_resolucion ? ` · motivo: ${i.motivo_resolucion}` : ""}</small></div>)}
          <h3>Decisiones registradas</h3>
          {d.decisiones.map((x) => <p key={x.id}><strong>{x.accion}</strong> por {x.revisor} — {x.fundamento}</p>)}
        </div>
      </div>
      {modal && <ModalDecision d={d} cerrar={() => setModal(false)} alDecidir={() => { setModal(false); void cargar(); }} />}
    </section>
  );
}

function ModalDecision({ d, cerrar, alDecidir }: { d: DetalleExpediente; cerrar: () => void; alDecidir: () => void }) {
  const [fundamento, setFundamento] = useState("");
  const [confirmo, setConfirmo] = useState(false);
  const [accion, setAccion] = useState("APROBAR");
  const [error, setError] = useState("");
  const idem = useMemo(() => crypto.randomUUID(), []);

  const enviar = async () => {
    setError("");
    try {
      await api.decidir(d.id_nube, { accion, fundamento, version_vista: d.version, confirmo_revision: confirmo }, idem);
      alDecidir();
    } catch (e) { setError(String(e)); }
  };
  const pendientes = d.incidencias.filter((i) => i.estado === "ABIERTA");

  return <div className="modal-fondo" role="dialog" aria-modal="true" aria-label="decisión de expediente"><div className="modal">
    <h2>Decisión de expediente</h2><p>Versión revisada: <strong>v{d.version}</strong>. Alertas pendientes: {pendientes.length}.</p>
    <label>Acción:{" "}<select value={accion} onChange={(e) => setAccion(e.target.value)}><option value="APROBAR">APROBAR</option><option value="RECHAZAR">RECHAZAR</option><option value="SOLICITAR_ANTECEDENTES">SOLICITAR ANTECEDENTES</option></select></label>
    <label className="casilla"><input type="checkbox" checked={confirmo} onChange={(e) => setConfirmo(e.target.checked)} /><span>He revisado las alertas y sus evidencias.</span></label>
    <textarea placeholder="Fundamento (obligatorio, mínimo 10 caracteres)" value={fundamento} onChange={(e) => setFundamento(e.target.value)} />
    {error && <p role="alert">{error}</p>}
    <div style={{ marginTop: "0.8rem", display: "flex", gap: "0.6rem" }}><button onClick={enviar} disabled={!confirmo || fundamento.trim().length < 10}>registrar decisión</button><button className="sec" onClick={cerrar}>cancelar</button></div>
  </div></div>;
}
